import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
  UserNotification, 
  UserNotificationCreateDto, 
  UserNotificationUpdateDto,
  UserNotificationResponse
} from "../../modules/shared/model";
import {
  CustomNotificationSettings,
  CustomNotificationSettingsCreateDto,
  CustomNotificationSettingsUpdateDto,
  CustomNotificationSettingsResponse,
  CustomNotificationSettingsLatestResponse
} from "../../modules/shared/model/custom-notification-settings.interfaces";
import {
  getUserNotifications as getUserNotificationsAction, 
  getUserNotificationsByUser as getUserNotificationsByUserAction,
  getUserNotificationById as getUserNotificationByIdAction,
  createUserNotificationAction,
  updateUserNotificationAction,
  deleteUserNotificationAction,
  bulkCreateUserNotificationsAction,
  bulkUpdateUserNotificationsAction,
  getUnreadNotificationCount as getUnreadNotificationCountAction,
  getLatestCustomNotificationSettings as getLatestCustomNotificationSettingsAction,
  getCustomNotificationSettings as getCustomNotificationSettingsAction,
  getCustomNotificationSettingsById as getCustomNotificationSettingsByIdAction,
  createCustomNotificationSettings as createCustomNotificationSettingsAction,
  updateCustomNotificationSettings as updateCustomNotificationSettingsAction,
  deleteCustomNotificationSettings as deleteCustomNotificationSettingsAction
} from "../Actions/UserNotificationActions";

// Async Thunks
export const fetchUserNotifications = createAsyncThunk("userNotifications/fetchAll", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
  try { 
    return await getUserNotificationsAction(query); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch user notifications"); 
  }
});

export const fetchUserNotificationsByUser = createAsyncThunk("userNotifications/fetchByUser", async ({ userId, query }: { userId: string; query?: Record<string, string | number | boolean> }, { rejectWithValue }) => {
  try { 
    return await getUserNotificationsByUserAction(userId, query); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch user notifications"); 
  }
});

export const fetchUserNotificationById = createAsyncThunk("userNotifications/fetchById", async (id: string, { rejectWithValue }) => {
  try { 
    return await getUserNotificationByIdAction(id); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch user notification"); 
  }
});

export const createUserNotification = createAsyncThunk("userNotifications/create", async (data: UserNotificationCreateDto, { rejectWithValue }) => {
  try { 
    return await createUserNotificationAction(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to create user notification"); 
  }
});

export const updateUserNotification = createAsyncThunk("userNotifications/update", async ({ id, data }: { id: string; data: UserNotificationUpdateDto }, { rejectWithValue }) => {
  try { 
    return await updateUserNotificationAction(id, data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to update user notification"); 
  }
});

export const deleteUserNotification = createAsyncThunk("userNotifications/delete", async (id: string, { rejectWithValue }) => {
  try { 
    await deleteUserNotificationAction(id); 
    return id; 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to delete user notification"); 
  }
});

export const bulkCreateUserNotifications = createAsyncThunk("userNotifications/bulkCreate", async (notifications: UserNotificationCreateDto[], { rejectWithValue }) => {
  try { 
    return await bulkCreateUserNotificationsAction(notifications); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to bulk create user notifications"); 
  }
});

export const bulkUpdateUserNotifications = createAsyncThunk("userNotifications/bulkUpdate", async (notifications: Array<{ id: string; data: UserNotificationUpdateDto }>, { rejectWithValue }) => {
  try { 
    return await bulkUpdateUserNotificationsAction(notifications); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to bulk update user notifications"); 
  }
});

export const fetchUnreadNotificationCount = createAsyncThunk("userNotifications/fetchUnreadCount", async (userId: string, { rejectWithValue }) => {
  try { 
    return await getUnreadNotificationCountAction(userId); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch unread notification count"); 
  }
});

// ===== CUSTOM NOTIFICATION SETTINGS ASYNC THUNKS =====

export const fetchLatestCustomNotificationSettings = createAsyncThunk("customNotificationSettings/fetchLatest", async (_, { rejectWithValue }) => {
  try { 
    return await getLatestCustomNotificationSettingsAction(); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch latest custom notification settings"); 
  }
});

export const fetchCustomNotificationSettings = createAsyncThunk("customNotificationSettings/fetchAll", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
  try { 
    return await getCustomNotificationSettingsAction(query); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch custom notification settings"); 
  }
});

export const fetchCustomNotificationSettingsById = createAsyncThunk("customNotificationSettings/fetchById", async (id: string, { rejectWithValue }) => {
  try { 
    return await getCustomNotificationSettingsByIdAction(id); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch custom notification settings"); 
  }
});

export const createCustomNotificationSettings = createAsyncThunk("customNotificationSettings/create", async (data: CustomNotificationSettingsCreateDto, { rejectWithValue }) => {
  try { 
    return await createCustomNotificationSettingsAction(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to create custom notification settings"); 
  }
});

export const updateCustomNotificationSettings = createAsyncThunk("customNotificationSettings/update", async ({ id, data }: { id: string; data: CustomNotificationSettingsUpdateDto }, { rejectWithValue }) => {
  try { 
    return await updateCustomNotificationSettingsAction(id, data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to update custom notification settings"); 
  }
});

export const deleteCustomNotificationSettings = createAsyncThunk("customNotificationSettings/delete", async (id: string, { rejectWithValue }) => {
  try { 
    await deleteCustomNotificationSettingsAction(id); 
    return id; 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to delete custom notification settings"); 
  }
});

// State interface
interface UserNotificationState {
  // All user notifications (paginated)
  userNotifications: UserNotification[];
  currentUserNotification: UserNotification | null;
  userNotificationsLoading: boolean;
  userNotificationsError: string | null;
  userNotificationsTotalCount?: number;
  userNotificationsCurrentPage: number;
  userNotificationsHasNextPage: boolean;
  userNotificationsHasPreviousPage: boolean;
  userNotificationsSearchQuery: string;
  
  // User-specific notifications
  userSpecificNotifications: UserNotification[];
  userSpecificNotificationsLoading: boolean;
  userSpecificNotificationsError: string | null;
  userSpecificNotificationsTotalCount?: number;
  userSpecificNotificationsCurrentPage: number;
  userSpecificNotificationsHasNextPage: boolean;
  userSpecificNotificationsHasPreviousPage: boolean;
  
  // Unread notification count
  unreadCount: number;
  unreadCountLoading: boolean;
  
  // Custom Notification Settings
  latestCustomNotificationSettings: CustomNotificationSettings | null;
  latestCustomNotificationSettingsLoading: boolean;
  latestCustomNotificationSettingsError: string | null;
  
  customNotificationSettings: CustomNotificationSettings[];
  currentCustomNotificationSettings: CustomNotificationSettings | null;
  customNotificationSettingsLoading: boolean;
  customNotificationSettingsError: string | null;
  customNotificationSettingsTotalCount?: number;
  customNotificationSettingsCurrentPage: number;
  customNotificationSettingsHasNextPage: boolean;
  customNotificationSettingsHasPreviousPage: boolean;
  customNotificationSettingsSearchQuery: string;
  
  // Form state
  isFormDirty: boolean;
  lastSavedAt: string | null;
}

const initialState: UserNotificationState = {
  // All user notifications
  userNotifications: [],
  currentUserNotification: null,
  userNotificationsLoading: false,
  userNotificationsError: null,
  userNotificationsTotalCount: 0,
  userNotificationsCurrentPage: 1,
  userNotificationsHasNextPage: false,
  userNotificationsHasPreviousPage: false,
  userNotificationsSearchQuery: "",
  
  // User-specific notifications
  userSpecificNotifications: [],
  userSpecificNotificationsLoading: false,
  userSpecificNotificationsError: null,
  userSpecificNotificationsTotalCount: 0,
  userSpecificNotificationsCurrentPage: 1,
  userSpecificNotificationsHasNextPage: false,
  userSpecificNotificationsHasPreviousPage: false,
  
  // Unread notification count
  unreadCount: 0,
  unreadCountLoading: false,
  
  // Custom Notification Settings
  latestCustomNotificationSettings: null,
  latestCustomNotificationSettingsLoading: false,
  latestCustomNotificationSettingsError: null,
  
  customNotificationSettings: [],
  currentCustomNotificationSettings: null,
  customNotificationSettingsLoading: false,
  customNotificationSettingsError: null,
  customNotificationSettingsTotalCount: 0,
  customNotificationSettingsCurrentPage: 1,
  customNotificationSettingsHasNextPage: false,
  customNotificationSettingsHasPreviousPage: false,
  customNotificationSettingsSearchQuery: "",
  
  // Form state
  isFormDirty: false,
  lastSavedAt: null,
};

const userNotificationSlice = createSlice({
  name: "userNotifications",
  initialState,
  reducers: {
    clearUserNotificationsError(state) { 
      state.userNotificationsError = null; 
    },
    clearUserSpecificNotificationsError(state) { 
      state.userSpecificNotificationsError = null; 
    },
    clearCurrentUserNotification(state) { 
      state.currentUserNotification = null; 
    },
    setUserNotificationsSearchQuery(state, action: PayloadAction<string>) { 
      state.userNotificationsSearchQuery = action.payload; 
    },
    setUserNotificationFormDirty(state, action: PayloadAction<boolean>) { 
      state.isFormDirty = action.payload; 
    },
    clearAllUserNotifications(state) {
      state.userNotifications = [];
      state.userSpecificNotifications = [];
      state.currentUserNotification = null;
      state.userNotificationsTotalCount = 0;
      state.userSpecificNotificationsTotalCount = 0;
    },
    updateUserNotificationFromForm(state, action: PayloadAction<Partial<UserNotification>>) {
      if (state.currentUserNotification) {
        state.currentUserNotification = { ...state.currentUserNotification, ...action.payload };
      }
    },
    // Custom Notification Settings Reducers
    clearLatestCustomNotificationSettingsError(state) { 
      state.latestCustomNotificationSettingsError = null; 
    },
    clearCustomNotificationSettingsError(state) { 
      state.customNotificationSettingsError = null; 
    },
    clearCurrentCustomNotificationSettings(state) { 
      state.currentCustomNotificationSettings = null; 
    },
    setCustomNotificationSettingsSearchQuery(state, action: PayloadAction<string>) { 
      state.customNotificationSettingsSearchQuery = action.payload; 
    },
    clearAllCustomNotificationSettings(state) {
      state.customNotificationSettings = [];
      state.currentCustomNotificationSettings = null;
      state.latestCustomNotificationSettings = null;
      state.customNotificationSettingsTotalCount = 0;
    },
    updateLatestCustomNotificationSettingsFromForm(state, action: PayloadAction<Partial<CustomNotificationSettings>>) {
      if (state.latestCustomNotificationSettings) {
        state.latestCustomNotificationSettings = { ...state.latestCustomNotificationSettings, ...action.payload };
      }
    },
    incrementUnreadCount(state) {
      state.unreadCount += 1;
    },
    decrementUnreadCount(state, action: PayloadAction<number>) {
      state.unreadCount = Math.max(0, state.unreadCount - action.payload);
    },
    setUnreadCount(state, action: PayloadAction<number>) {
      state.unreadCount = action.payload;
    }
  },
  extraReducers: (builder) => {
    // All User Notifications Extra Reducers
    builder
      .addCase(fetchUserNotifications.pending, (state) => { 
        state.userNotificationsLoading = true; 
        state.userNotificationsError = null; 
      })
      .addCase(fetchUserNotifications.fulfilled, (state, action: PayloadAction<UserNotificationResponse>) => { 
        state.userNotificationsLoading = false; 
        state.userNotifications = action.payload.data || []; 
        state.userNotificationsTotalCount = action.payload.totalCount || 0; 
        state.userNotificationsCurrentPage = action.payload.page || 1;
        state.userNotificationsHasNextPage = action.payload.hasNextPage || false;
        state.userNotificationsHasPreviousPage = action.payload.hasPreviousPage || false;
      })
      .addCase(fetchUserNotifications.rejected, (state, action: any) => { 
        state.userNotificationsLoading = false; 
        state.userNotificationsError = action.payload as string; 
      });

    // User-Specific Notifications Extra Reducers
    builder
      .addCase(fetchUserNotificationsByUser.pending, (state) => { 
        state.userSpecificNotificationsLoading = true; 
        state.userSpecificNotificationsError = null; 
      })
      .addCase(fetchUserNotificationsByUser.fulfilled, (state, action: PayloadAction<UserNotificationResponse>) => { 
        state.userSpecificNotificationsLoading = false; 
        state.userSpecificNotifications = action.payload.data || []; 
        state.userSpecificNotificationsTotalCount = action.payload.totalCount || 0; 
        state.userSpecificNotificationsCurrentPage = action.payload.page || 1;
        state.userSpecificNotificationsHasNextPage = action.payload.hasNextPage || false;
        state.userSpecificNotificationsHasPreviousPage = action.payload.hasPreviousPage || false;
      })
      .addCase(fetchUserNotificationsByUser.rejected, (state, action: any) => { 
        state.userSpecificNotificationsLoading = false; 
        state.userSpecificNotificationsError = action.payload as string; 
      });

    builder
      .addCase(fetchUserNotificationById.pending, (state) => { 
        state.userNotificationsLoading = true; 
        state.userNotificationsError = null; 
      })
      .addCase(fetchUserNotificationById.fulfilled, (state, action: PayloadAction<UserNotification>) => { 
        state.userNotificationsLoading = false; 
        state.currentUserNotification = action.payload; 
      })
      .addCase(fetchUserNotificationById.rejected, (state, action: any) => { 
        state.userNotificationsLoading = false; 
        state.userNotificationsError = action.payload as string; 
      });

    // Create Extra Reducers
    builder
      .addCase(createUserNotification.pending, (state) => { 
        state.userNotificationsLoading = true; 
        state.userNotificationsError = null; 
      })
      .addCase(createUserNotification.fulfilled, (state, action: PayloadAction<UserNotification>) => { 
        state.userNotificationsLoading = false; 
        state.userNotifications.unshift(action.payload); 
        state.userNotificationsTotalCount = (state.userNotificationsTotalCount || 0) + 1; 
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(createUserNotification.rejected, (state, action: any) => { 
        state.userNotificationsLoading = false; 
        state.userNotificationsError = action.payload as string; 
      });

    // Update Extra Reducers
    builder
      .addCase(updateUserNotification.pending, (state) => { 
        state.userNotificationsLoading = true; 
        state.userNotificationsError = null; 
      })
      .addCase(updateUserNotification.fulfilled, (state, action: PayloadAction<UserNotification>) => {
        state.userNotificationsLoading = false;
        const index = state.userNotifications.findIndex(x => x.id === action.payload.id);
        if (index !== -1) state.userNotifications[index] = action.payload;
        if (state.currentUserNotification?.id === action.payload.id) state.currentUserNotification = action.payload;
        
        // Update user-specific notifications as well
        const userSpecificIndex = state.userSpecificNotifications.findIndex(x => x.id === action.payload.id);
        if (userSpecificIndex !== -1) state.userSpecificNotifications[userSpecificIndex] = action.payload;
        
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(updateUserNotification.rejected, (state, action: any) => { 
        state.userNotificationsLoading = false; 
        state.userNotificationsError = action.payload as string; 
      });

    // Delete Extra Reducers
    builder
      .addCase(deleteUserNotification.pending, (state) => { 
        state.userNotificationsLoading = true; 
        state.userNotificationsError = null; 
      })
      .addCase(deleteUserNotification.fulfilled, (state, action: PayloadAction<string>) => { 
        state.userNotificationsLoading = false; 
        state.userNotifications = state.userNotifications.filter(x => x.id !== action.payload); 
        if (state.currentUserNotification?.id === action.payload) state.currentUserNotification = null; 
        state.userNotificationsTotalCount = Math.max(0, (state.userNotificationsTotalCount || 0) - 1); 
      })
      .addCase(deleteUserNotification.rejected, (state, action: any) => { 
        state.userNotificationsLoading = false; 
        state.userNotificationsError = action.payload as string; 
      });

    // Bulk Create Extra Reducers
    builder
      .addCase(bulkCreateUserNotifications.pending, (state) => { 
        state.userNotificationsLoading = true; 
        state.userNotificationsError = null; 
      })
      .addCase(bulkCreateUserNotifications.fulfilled, (state, action: PayloadAction<UserNotification[]>) => { 
        state.userNotificationsLoading = false; 
        state.userNotifications.unshift(...action.payload); 
        state.userNotificationsTotalCount = (state.userNotificationsTotalCount || 0) + action.payload.length; 
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(bulkCreateUserNotifications.rejected, (state, action: any) => { 
        state.userNotificationsLoading = false; 
        state.userNotificationsError = action.payload as string; 
      });

    // Bulk Update Extra Reducers
    builder
      .addCase(bulkUpdateUserNotifications.pending, (state) => { 
        state.userNotificationsLoading = true; 
        state.userNotificationsError = null; 
      })
      .addCase(bulkUpdateUserNotifications.fulfilled, (state, action: PayloadAction<UserNotification[]>) => {
        state.userNotificationsLoading = false;
        action.payload.forEach(updatedNotification => {
          const index = state.userNotifications.findIndex(x => x.id === updatedNotification.id);
          if (index !== -1) state.userNotifications[index] = updatedNotification;
          if (state.currentUserNotification?.id === updatedNotification.id) state.currentUserNotification = updatedNotification;
          
          // Update user-specific notifications as well
          const userSpecificIndex = state.userSpecificNotifications.findIndex(x => x.id === updatedNotification.id);
          if (userSpecificIndex !== -1) state.userSpecificNotifications[userSpecificIndex] = updatedNotification;
        });
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(bulkUpdateUserNotifications.rejected, (state, action: any) => { 
        state.userNotificationsLoading = false; 
        state.userNotificationsError = action.payload as string; 
      });

    // ===== CUSTOM NOTIFICATION SETTINGS EXTRA REDUCERS =====

    // Latest Custom Notification Settings Extra Reducers
    builder
      .addCase(fetchLatestCustomNotificationSettings.pending, (state) => { 
        state.latestCustomNotificationSettingsLoading = true; 
        state.latestCustomNotificationSettingsError = null; 
      })
      .addCase(fetchLatestCustomNotificationSettings.fulfilled, (state, action: PayloadAction<CustomNotificationSettingsLatestResponse>) => { 
        state.latestCustomNotificationSettingsLoading = false; 
        state.latestCustomNotificationSettings = action.payload; 
        state.lastSavedAt = new Date().toISOString();
      })
      .addCase(fetchLatestCustomNotificationSettings.rejected, (state, action: any) => { 
        state.latestCustomNotificationSettingsLoading = false; 
        state.latestCustomNotificationSettingsError = action.payload as string; 
      });

    // All Custom Notification Settings Extra Reducers
    builder
      .addCase(fetchCustomNotificationSettings.pending, (state) => { 
        state.customNotificationSettingsLoading = true; 
        state.customNotificationSettingsError = null; 
      })
      .addCase(fetchCustomNotificationSettings.fulfilled, (state, action: PayloadAction<CustomNotificationSettingsResponse>) => { 
        state.customNotificationSettingsLoading = false; 
        state.customNotificationSettings = action.payload.data || []; 
        state.customNotificationSettingsTotalCount = action.payload.totalCount || 0; 
        state.customNotificationSettingsCurrentPage = action.payload.page || 1;
        state.customNotificationSettingsHasNextPage = action.payload.hasNextPage || false;
        state.customNotificationSettingsHasPreviousPage = action.payload.hasPreviousPage || false;
      })
      .addCase(fetchCustomNotificationSettings.rejected, (state, action: any) => { 
        state.customNotificationSettingsLoading = false; 
        state.customNotificationSettingsError = action.payload as string; 
      });

    builder
      .addCase(fetchCustomNotificationSettingsById.pending, (state) => { 
        state.customNotificationSettingsLoading = true; 
        state.customNotificationSettingsError = null; 
      })
      .addCase(fetchCustomNotificationSettingsById.fulfilled, (state, action: PayloadAction<CustomNotificationSettings>) => { 
        state.customNotificationSettingsLoading = false; 
        state.currentCustomNotificationSettings = action.payload; 
      })
      .addCase(fetchCustomNotificationSettingsById.rejected, (state, action: any) => { 
        state.customNotificationSettingsLoading = false; 
        state.customNotificationSettingsError = action.payload as string; 
      });

    // Create Custom Notification Settings Extra Reducers
    builder
      .addCase(createCustomNotificationSettings.pending, (state) => { 
        state.customNotificationSettingsLoading = true; 
        state.customNotificationSettingsError = null; 
      })
      .addCase(createCustomNotificationSettings.fulfilled, (state, action: PayloadAction<CustomNotificationSettings>) => { 
        state.customNotificationSettingsLoading = false; 
        state.customNotificationSettings.unshift(action.payload); 
        state.customNotificationSettingsTotalCount = (state.customNotificationSettingsTotalCount || 0) + 1; 
        state.latestCustomNotificationSettings = action.payload; // Update latest settings
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(createCustomNotificationSettings.rejected, (state, action: any) => { 
        state.customNotificationSettingsLoading = false; 
        state.customNotificationSettingsError = action.payload as string; 
      });

    // Update Custom Notification Settings Extra Reducers
    builder
      .addCase(updateCustomNotificationSettings.pending, (state) => { 
        state.customNotificationSettingsLoading = true; 
        state.customNotificationSettingsError = null; 
      })
      .addCase(updateCustomNotificationSettings.fulfilled, (state, action: PayloadAction<CustomNotificationSettings>) => {
        state.customNotificationSettingsLoading = false;
        const index = state.customNotificationSettings.findIndex(x => x.id === action.payload.id);
        if (index !== -1) state.customNotificationSettings[index] = action.payload;
        if (state.currentCustomNotificationSettings?.id === action.payload.id) state.currentCustomNotificationSettings = action.payload;
        state.latestCustomNotificationSettings = action.payload; // Update latest settings
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(updateCustomNotificationSettings.rejected, (state, action: any) => { 
        state.customNotificationSettingsLoading = false; 
        state.customNotificationSettingsError = action.payload as string; 
      });

    // Delete Custom Notification Settings Extra Reducers
    builder
      .addCase(deleteCustomNotificationSettings.pending, (state) => { 
        state.customNotificationSettingsLoading = true; 
        state.customNotificationSettingsError = null; 
      })
      .addCase(deleteCustomNotificationSettings.fulfilled, (state, action: PayloadAction<string>) => { 
        state.customNotificationSettingsLoading = false; 
        state.customNotificationSettings = state.customNotificationSettings.filter(x => x.id !== action.payload); 
        if (state.currentCustomNotificationSettings?.id === action.payload) state.currentCustomNotificationSettings = null; 
        if (state.latestCustomNotificationSettings?.id === action.payload) state.latestCustomNotificationSettings = null;
        state.customNotificationSettingsTotalCount = Math.max(0, (state.customNotificationSettingsTotalCount || 0) - 1); 
      })
      .addCase(deleteCustomNotificationSettings.rejected, (state, action: any) => { 
        state.customNotificationSettingsLoading = false; 
        state.customNotificationSettingsError = action.payload as string; 
      });

    // Unread notification count reducers
    builder
      .addCase(fetchUnreadNotificationCount.pending, (state) => {
        state.unreadCountLoading = true;
      })
      .addCase(fetchUnreadNotificationCount.fulfilled, (state, action: PayloadAction<number>) => {
        state.unreadCountLoading = false;
        state.unreadCount = action.payload;
      })
      .addCase(fetchUnreadNotificationCount.rejected, (state) => {
        state.unreadCountLoading = false;
      });
  }
});

export const { 
  clearUserNotificationsError,
  clearUserSpecificNotificationsError,
  clearCurrentUserNotification, 
  setUserNotificationsSearchQuery,
  setUserNotificationFormDirty,
  clearAllUserNotifications,
  updateUserNotificationFromForm,
  // Custom Notification Settings Actions
  clearLatestCustomNotificationSettingsError,
  clearCustomNotificationSettingsError,
  clearCurrentCustomNotificationSettings,
  setCustomNotificationSettingsSearchQuery,
  clearAllCustomNotificationSettings,
  updateLatestCustomNotificationSettingsFromForm,
  // Unread count actions
  incrementUnreadCount,
  decrementUnreadCount,
  setUnreadCount
} = userNotificationSlice.actions;

export default userNotificationSlice.reducer;

